import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (user) => {
    if (!user) { setUserData(null); return; }
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        let calculatedPixels = data.pixels || 0;
        
        // Dynamically compute historical pixels for students to fix 0 balances
        if (data.role !== 'admin' && data.role !== 'instructor') {
          try {
            const qEnr = query(collection(db, 'enrollments'), where('userId', '==', user.uid));
            const enrSnap = await getDocs(qEnr);
            let enrPixels = 0;
            enrSnap.forEach(d => {
               enrPixels += Math.floor((d.data().pointsEarned || 0) / 10);
            });
            
            const qSub = query(collection(db, 'submissions'), where('studentId', '==', user.uid), where('status', '==', 'graded'));
            const subSnap = await getDocs(qSub);
            let subPixels = 0;
            subSnap.forEach(d => {
               subPixels += d.data().earnedPixels !== undefined ? d.data().earnedPixels : Math.floor((d.data().grade || 0) / 10);
            });
            
            const totalHistorical = enrPixels + subPixels;
            if (totalHistorical > calculatedPixels) {
               calculatedPixels = totalHistorical;
               // Silently sync the missing pixels in the DB to avoid future recalculation delays
               updateDoc(userDocRef, { pixels: calculatedPixels }).catch(e => console.error("Could not sync pixels:", e));
            }
          } catch(e) {
            console.error("Error computing pixels", e);
          }
        }
        
        setUserData({ ...data, pixels: calculatedPixels });
      } else {
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserData(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await fetchUserData(user);
      setLoading(false);
    });
    return unsubscribe;
  }, [fetchUserData]);

  // Call this after updating user profile in Firestore
  const refreshUserData = useCallback(async () => {
    if (currentUser) await fetchUserData(currentUser);
  }, [currentUser, fetchUserData]);

  // Profile is complete when these required fields exist
  const isProfileComplete = !!(
    userData?.nameAr?.trim() &&
    userData?.nameEn?.trim() &&
    userData?.username?.trim() &&
    userData?.phone?.trim()
  );

  const isAdmin = userData?.role === 'admin';
  const isInstructor = userData?.role === 'instructor';
  const instructorStatus = userData?.instructorData?.status || 'incomplete';

  const value = {
    currentUser,
    userData,
    loading,
    refreshUserData,
    isProfileComplete,
    emailVerified: currentUser?.emailVerified ?? true, // Google users are always verified
    isAdmin,
    isInstructor,
    instructorStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
